/**
 * Compare two version strings.
 * Returns:
 *  -1 if a < b
 *   0 if a == b
 *  +1 if a > b
 *
 * Supported format examples:
 *   1.2.3
 *   1.2.3-alpha
 *   1.2.3-alpha1
 *   1.2.3-beta
 *   1.2.3-beta2
 *   1.2.3-rc
 *   1.2.3-rc4
 *   2.31.00-beta1
 *
 * Ordering rules:
 *   - Compare numeric core segments left-to-right (missing segments = 0)
 *   - Pre-release < final (no pre-release)
 *   - alpha < beta < rc < (final)
 *   - Same pre-release tag: compare numeric suffix (alpha1 > alpha)
 *   - Unknown pre-release tags: lexical compare of whole tag
 */
export const compareVersions = (a: string, b: string): number => {
  if (a === b) return 0

  const parseVersion = (version: string) => {
    // Split only at first '-' to preserve tags like beta1
    const hyphenIndex = version.indexOf('-')
    const corePart =
      hyphenIndex === -1 ? version : version.slice(0, hyphenIndex)
    const preReleasePart =
      hyphenIndex === -1 ? '' : version.slice(hyphenIndex + 1)

    const coreSegments = corePart
      .split('.')
      .map((segment) => Number.parseInt(segment, 10) || 0)

    return { coreSegments, preReleasePart }
  }

  const parsePreRelease = (pre: string) => {
    if (!pre) {
      return {
        tag: '',
        number: 0,
        isFinal: true,
      }
    }

    // Extract leading letters as tag, trailing digits as number
    const match = pre.match(/^([a-zA-Z]+)(\d*)$/)
    if (match) {
      const tag = match[1].toLowerCase()
      const number = match[2] ? Number.parseInt(match[2], 10) : 0
      return { tag, number, isFinal: false }
    }

    // Fallback: treat the whole pre string as unknown tag
    return { tag: pre.toLowerCase(), number: 0, isFinal: false }
  }

  const PRERELEASE_ORDER: Record<string, number> = {
    alpha: 0,
    beta: 1,
    rc: 2,
  }

  const left = parseVersion(a)
  const right = parseVersion(b)

  // 1. Compare core numeric segments
  const maxLen = Math.max(left.coreSegments.length, right.coreSegments.length)
  for (let i = 0; i < maxLen; i++) {
    const leftNum = left.coreSegments[i] ?? 0
    const rightNum = right.coreSegments[i] ?? 0
    if (leftNum < rightNum) return -1
    if (leftNum > rightNum) return 1
  }

  // 2. Core equal -> compare pre-release parts
  const leftPre = parsePreRelease(left.preReleasePart)
  const rightPre = parsePreRelease(right.preReleasePart)

  // Final (no prerelease) is greater
  if (leftPre.isFinal && rightPre.isFinal) return 0
  if (leftPre.isFinal) return 1
  if (rightPre.isFinal) return -1

  const leftOrder =
    PRERELEASE_ORDER[leftPre.tag] ?? Number.POSITIVE_INFINITY
  const rightOrder =
    PRERELEASE_ORDER[rightPre.tag] ?? Number.POSITIVE_INFINITY

  if (leftOrder < rightOrder) return -1
  if (leftOrder > rightOrder) return 1

  // Same known tag or both unknown (both mapped to same order):
  if (leftPre.tag !== rightPre.tag) {
    // Both unknown tags: lexical compare
    return leftPre.tag < rightPre.tag ? -1 : 1
  }

  // Same tag: compare numeric suffix
  if (leftPre.number < rightPre.number) return -1
  if (leftPre.number > rightPre.number) return 1

  return 0
}